import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getInitials } from "../../../utils/util";
import Skeleton from "react-loading-skeleton";
import axios from "axios";
import { useParams } from "react-router-dom";
import apiClient from "../../../apiClient/index";
import {
  DASHBOARD_SITE_STATS,
  FETCH_INSPECTION_TREND,
  FETCH_REGION_WISE_SCORE,
  FETCH_SITE_NEGATIVE_POSTIVE_STATS,
  FETCH_SUB_SITE_WITH_GROUPID,
  USER_WISE_INSPECTION_REPORT,
} from "../../../apiClient/endpoints";
import BarChart from "../../Charts/BarChart";
import ResponseChart from "../../Charts/ResponseChart";
import MachineChart from "../../Charts/MachineDistribution";
import InspectionTrendChart from "../../Charts/InspectionTrendChart";
import HorizontalBarChart from "../../Charts/HorizontalBarChart";
import SiteChartCard from "../../dashboard/SiteChartCard";
import GroupList from "../../GroupList";
import Filter from "./Filter";
import DateRangeFilterV2 from "../../dashboard/DateRangeFilterV2";
import NegativeResponsesV2 from "../../dashboard/NegativeResponsesV2";
import GroupWiseReportV2 from "../../dashboard/GroupWiseReportV2";
import SiteWiseInspectionStatsV2 from "./SiteWiseInspectionStatsV2";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import GradeOutlinedIcon from "@mui/icons-material/GradeOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import {
  aggregateTopSitesByFindings,
  buildFilterPayload,
  getDefaultChartDates,
  limitTopSiteResponses,
  parseTrendApiResponse,
  toApiDates,
  toAxiosDates,
} from "../../../utils/siteChartUtils";

const getFilters = (options = []) => [
  {
    name: "Inspection Type",
    tags:
      options?.mainSites?.map((ele) => ({
        name: ele?.name,
        value: ele?.uuid,
        key: "inspectionType",
      })) || [],
  },
  {
    name: "Site",
    tags:
      options?.sites?.map((ele) => ({
        name: ele?.name,
        value: ele?.uuid,
        key: "site",
      })) || [],
  },
  {
    name: "OBR Org",
    tags:
      options?.obrOrg?.map((ele) => ({
        name: ele?.name,
        value: ele?.value,
        key: "obrOrg",
      })) || [],
  },
  {
    name: "OBR Bu",
    tags:
      options?.obrBu?.map((ele) => ({
        name: ele?.name,
        value: ele?.value,
        key: "obrBu",
      })) || [],
  },
];

const defaultChartFilterState = () => ({
  dates: getDefaultChartDates(),
  selectedTags: [],
  granularity: "weekly",
  metric: "inspectionCount",
  topN: 10,
});

const SiteDashboardV2 = () => {
  const userData = useSelector((state) => state.userSlice);
  const { groupId } = useParams();
  const configData = useSelector((state) => state.configSlice);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [regionResponseStats, setRegionResponseStats] = useState([]);
  const [options, setOptions] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [groupInfo, setGroupInfo] = useState(null);
  const [dates, setDates] = useState({ start: null, end: null });
  const [list, setList] = useState([]);
  const [siteStats, setSiteStats] = useState({});
  const [userWiseInspectionData, setUserWiseInspectionData] = useState([]);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const [inspectionDataLoading, setInspectionDataLoading] = useState(true);
  const [resetStateFlag, setResetStateFlag] = useState(false);

  const [trendFilters, setTrendFilters] = useState(defaultChartFilterState);
  const [findingsFilters, setFindingsFilters] = useState(defaultChartFilterState);
  const [responseFilters, setResponseFilters] = useState(defaultChartFilterState);
  const [healthFilters, setHealthFilters] = useState(defaultChartFilterState);
  const [regionFilters, setRegionFilters] = useState({ dates: getDefaultChartDates(), selectedTags: [] });

  const [trendData, setTrendData] = useState([]);
  const [findingsData, setFindingsData] = useState([]);
  const [responseStats, setResponseStats] = useState([]);
  const [siteHealth, setSiteHealth] = useState({
    healthyCount: 0,
    unhealthyCount: 0,
    noRecordsCount: 0,
  });

  const [chartsLoading, setChartsLoading] = useState({
    trend: true,
    findings: true,
    response: true,
    health: true,
    region: true,
  });

  const filterOptions = getFilters(options);

  const filterPayload = () => ({
    site: selectedCheckboxes?.filter((e) => e.key === "site").map((e) => e.value) || [],
    obrBu: selectedCheckboxes?.filter((e) => e.key === "obrBu").map((e) => e.value) || [],
    obrOrg: selectedCheckboxes?.filter((e) => e.key === "obrOrg").map((e) => e.value) || [],
    mainSites: selectedCheckboxes?.filter((e) => e.key === "inspectionType").map((e) => e.value) || [],
  });

  const fetchGroupDetail = async () => {
    setGroupInfo(null);
    const res = await apiClient.get(`${configData.config.UNO_URL}/api/user/group/details/${groupId}`, {
      headers: { Authorization: `Bearer ${userData?.token}` },
    });
    res ? setGroupInfo(res) : setGroupInfo({});
  };

  const fetchNegativeResponses = async () => {
    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/dashboard/report/negative/responses/${groupId}`,
        {
          machineIds: [],
          dates: {
            start: dates.start ? dates.start.toISOString() : null,
            end: dates.start ? dates.end.toISOString() : null,
          },
          isSite: true,
          filters: filterPayload(),
        },
        { headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" } },
      );
      setQuestions(data.data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const fetchNegativeResponsesGroupwise = async () => {
    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/dashboard/report/negative/responses/byGroup/${groupId}`,
        {
          machineIds: [],
          dates: {
            start: dates.start ? dates.start.toISOString() : null,
            end: dates.start ? dates.end.toISOString() : null,
          },
          isSite: true,
          filters: filterPayload(),
        },
        { headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" } },
      );
      setList(data.data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const fetchSiteStats = async () => {
    try {
      const res = await apiClient.post(`${configData.config.API_URL}${DASHBOARD_SITE_STATS}/${groupId}`, {
        dates: {
          startDate: dates.start ? dates.start.toISOString() : null,
          endDate: dates.start ? dates.end.toISOString() : null,
        },
        filters: filterPayload(),
      });
      setSiteStats(res);
    } catch (error) {
      console.log(error.message);
    }
  };

  const fetcSiteFilters = async () => {
    try {
      const res = await apiClient.get(`${configData.config.API_URL}${FETCH_SUB_SITE_WITH_GROUPID}/${groupId}`);
      setOptions(res);
    } catch (error) {
      console.log(error.message);
    }
  };

  const userWiseInspectionReport = async () => {
    try {
      setInspectionDataLoading(true);
      const res = await apiClient.post(`${configData.config.API_URL}${USER_WISE_INSPECTION_REPORT}/${groupId}`, {
        dates: {
          startDate: dates.start ? dates.start.toISOString() : null,
          endDate: dates.start ? dates.end.toISOString() : null,
        },
        filters: filterPayload(),
      });
      setUserWiseInspectionData(res);
    } catch (error) {
      console.log(error.message);
    } finally {
      setInspectionDataLoading(false);
    }
  };

  const fetchTrendChart = async (filters = trendFilters) => {
    if (!groupId) return;
    setChartsLoading((prev) => ({ ...prev, trend: true }));

    const body = {
      dates: toApiDates(filters.dates),
      granularity: filters.granularity || "weekly",
      metric: filters.metric || "inspectionCount",
      filters: { mainSites: buildFilterPayload(filters.selectedTags).mainSites },
      trend: true,
    };
    const headers = { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" };
    const endpoints = [
      `${configData.config.API_URL}${FETCH_INSPECTION_TREND}/${groupId}`,
      `${configData.config.API_URL}${DASHBOARD_SITE_STATS}/${groupId}`,
      `${configData.config.API_URL}${USER_WISE_INSPECTION_REPORT}/${groupId}`,
    ];

    try {
      let trendRows = null;

      for (const url of endpoints) {
        if (trendRows) break;
        try {
          const { data } = await axios.post(url, body, { headers });
          trendRows = parseTrendApiResponse(data);
        } catch (requestError) {
          console.warn("Trend API attempt failed:", url, requestError?.response?.status);
        }
      }

      setTrendData(trendRows || []);
    } catch (error) {
      console.error("Trend chart error:", error?.response?.status, error?.response?.data);
      setTrendData([]);
    } finally {
      setChartsLoading((prev) => ({ ...prev, trend: false }));
    }
  };

  const fetchFindingsChart = async (filters = findingsFilters) => {
    if (!groupId) return;
    setChartsLoading((prev) => ({ ...prev, findings: true }));
    try {
      const res = await apiClient.post(`${configData.config.API_URL}${USER_WISE_INSPECTION_REPORT}/${groupId}`, {
        dates: toApiDates(filters.dates),
        filters: buildFilterPayload(filters.selectedTags),
      });
      setFindingsData(aggregateTopSitesByFindings(res, filters.topN || 10));
    } catch (error) {
      console.log(error);
      setFindingsData([]);
    } finally {
      setChartsLoading((prev) => ({ ...prev, findings: false }));
    }
  };

  const fetchResponseChart = async (filters = responseFilters) => {
    if (!groupId) return;
    setChartsLoading((prev) => ({ ...prev, response: true }));
    try {
      const res = await apiClient.post(`${configData.config.API_URL}${FETCH_SITE_NEGATIVE_POSTIVE_STATS}/${groupId}`, {
        dates: toApiDates(filters.dates),
        filters: buildFilterPayload(filters.selectedTags),
      });
      setResponseStats(limitTopSiteResponses(res, 12));
    } catch (error) {
      console.log(error);
      setResponseStats([]);
    } finally {
      setChartsLoading((prev) => ({ ...prev, response: false }));
    }
  };

  const fetchHealthChart = async (filters = healthFilters) => {
    if (!groupId) return;
    setChartsLoading((prev) => ({ ...prev, health: true }));
    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/dashboard/report/machine/stats/${groupId}`,
        {
          machineIds: [],
          isSite: true,
          dates: toAxiosDates(filters.dates),
          filters: buildFilterPayload(filters.selectedTags),
        },
        { headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" } },
      );
      setSiteHealth(data.data || { healthyCount: 0, unhealthyCount: 0, noRecordsCount: 0 });
    } catch (error) {
      console.log(error);
      setSiteHealth({ healthyCount: 0, unhealthyCount: 0, noRecordsCount: 0 });
    } finally {
      setChartsLoading((prev) => ({ ...prev, health: false }));
    }
  };

  const fetchRegionChart = async (filters = regionFilters) => {
    if (!groupId) return;
    setChartsLoading((prev) => ({ ...prev, region: true }));
    try {
      const res = await apiClient.post(`${configData.config.API_URL}${FETCH_REGION_WISE_SCORE}/${groupId}`, {
        dates: toApiDates(filters.dates),
        filters: buildFilterPayload(filters.selectedTags),
      });
      setRegionResponseStats(res || []);
    } catch (error) {
      console.log(error);
      setRegionResponseStats([]);
    } finally {
      setChartsLoading((prev) => ({ ...prev, region: false }));
    }
  };

  const fetchAllCharts = async (overrides = {}) => {
    await Promise.all([
      fetchTrendChart(overrides.trend || trendFilters),
      fetchFindingsChart(overrides.findings || findingsFilters),
      fetchResponseChart(overrides.response || responseFilters),
      fetchHealthChart(overrides.health || healthFilters),
      fetchRegionChart(overrides.region || regionFilters),
    ]);
  };

  const Reload = async () => {
    setLoading(true);
    await Promise.all([
      fetchSiteStats(),
      fetchNegativeResponses(),
      fetchNegativeResponsesGroupwise(),
      userWiseInspectionReport(),
    ]);
    setLoading(false);
  };

  const RefreshPage = async () => {
    setLoading(true);
    setUserWiseInspectionData([]);
    const defaults = defaultChartFilterState();
    const regionDefaults = { dates: getDefaultChartDates(), selectedTags: [] };
    setTrendFilters(defaults);
    setFindingsFilters(defaults);
    setResponseFilters(defaults);
    setHealthFilters(defaults);
    setRegionFilters(regionDefaults);
    await Promise.all([fetchGroupDetail(), fetcSiteFilters()]);
    setDates({ start: null, end: null });
    await Reload();
    await fetchAllCharts({
      trend: defaults,
      findings: defaults,
      response: defaults,
      health: defaults,
      region: regionDefaults,
    });
  };

  useEffect(() => {
    if (!loading) Reload();
  }, [dates]);

  useEffect(() => {
    if (!groupId?.length) return;
    setActiveTab("dashboard");
    RefreshPage();
  }, [groupId]);

  useEffect(() => {
    if (resetStateFlag) Reload();
  }, [resetStateFlag]);

  const healthTotal = siteHealth.healthyCount + siteHealth.unhealthyCount + siteHealth.noRecordsCount;

  return (
    <div className="dashboard-v2 site-dashboard-v2">
      <div className="dashboard-v2__top">
        <div className="machines-v2__tabs dashboard-v2__tabs site-dashboard-v2__tabs">
          <button
            type="button"
            className={`machines-v2__tab ${activeTab === "dashboard" ? "active" : ""}`}
            onClick={() => setActiveTab("dashboard")}
          >
            Site Dashboard
          </button>
          <button
            type="button"
            className={`machines-v2__tab ${activeTab === "groups" ? "active" : ""}`}
            onClick={() => setActiveTab("groups")}
          >
            Groups
          </button>
          <button
            type="button"
            className={`machines-v2__tab ${activeTab === "inspection" ? "active" : ""}`}
            onClick={() => setActiveTab("inspection")}
          >
            Site Wise Inspection Report
          </button>
        </div>

        {activeTab !== "groups" && (
          <div className="dashboard-v2__filters">
            <DateRangeFilterV2 dates={dates} setDates={setDates} />
            <Filter
              variant="v2"
              filterOptions={filterOptions}
              selectedCheckboxes={selectedCheckboxes}
              setSelectedCheckboxes={setSelectedCheckboxes}
              clickHandler={Reload}
              setResetStateFlag={setResetStateFlag}
            />
          </div>
        )}
      </div>

      {activeTab === "groups" ? (
        <div className="machines-v2__groups">
          <GroupList setActiveTab={() => setActiveTab("dashboard")} isSite={true} navigateLink="/s/dashboard" />
        </div>
      ) : activeTab === "inspection" ? (
        <SiteWiseInspectionStatsV2 loading={inspectionDataLoading} data={userWiseInspectionData} />
      ) : (
        <>
          <div className="dashboard-v2__stats site-dashboard-v2__stats">
            <article className="dashboard-v2__client-card">
              {loading ? (
                <Skeleton width={56} height={56} circle />
              ) : groupInfo?.avatar ? (
                <img src={groupInfo.avatar} alt="" className="dashboard-v2__client-logo" referrerPolicy="no-referrer" />
              ) : (
                <div className="dashboard-v2__client-initials">{groupInfo?.name ? getInitials(groupInfo.name) : ""}</div>
              )}
              <div>
                <strong>{loading ? <Skeleton width={120} /> : groupInfo?.name}</strong>
                <span>Client / Site</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--purple">
                <AssignmentOutlinedIcon />
              </div>
              <div>
                <strong>{loading ? "—" : siteStats?.inspectionCount || 0}</strong>
                <span>Inspection Count</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--green">
                <GradeOutlinedIcon />
              </div>
              <div>
                <strong>{loading ? "—" : siteStats?.score || 0}</strong>
                <span>Score</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--amber">
                <ReportProblemOutlinedIcon />
              </div>
              <div>
                <strong>{loading ? "—" : siteStats?.findingCount || 0}</strong>
                <span>Finding Count</span>
              </div>
            </article>
          </div>

          <div className="dashboard-v2__charts site-dashboard-v2__charts">
            <SiteChartCard
              title="Inspection Trend Over Time"
              loading={chartsLoading.trend}
              isEmpty={!trendData?.length}
              filterFields={["date", "granularity", "metric", "inspectionType"]}
              filterValues={trendFilters}
              onFilterChange={setTrendFilters}
              onFilterApply={fetchTrendChart}
              filterOptions={filterOptions}
            >
              <InspectionTrendChart
                list={trendData}
                metric={trendFilters.metric}
                granularity={trendFilters.granularity}
                compact
              />
            </SiteChartCard>

            <SiteChartCard
              title="Top Sites by Finding Count"
              loading={chartsLoading.findings}
              isEmpty={!findingsData?.length}
              filterFields={["date", "topN", "inspectionType", "obrOrg", "obrBu"]}
              filterValues={findingsFilters}
              onFilterChange={setFindingsFilters}
              onFilterApply={fetchFindingsChart}
              filterOptions={filterOptions}
            >
              <HorizontalBarChart data={findingsData} xKey="name" yKey="count" chartName="Findings" compact />
            </SiteChartCard>

            <SiteChartCard
              title="Site-wise Negative vs Positive"
              subtitle="Top 12 sites by total responses"
              loading={chartsLoading.response}
              isEmpty={!responseStats?.length}
              filterFields={["date", "inspectionType", "site", "obrOrg", "obrBu"]}
              filterValues={responseFilters}
              onFilterChange={setResponseFilters}
              onFilterApply={fetchResponseChart}
              filterOptions={filterOptions}
            >
              <ResponseChart list={responseStats} compact />
            </SiteChartCard>

            <SiteChartCard
              title="Site Health Distribution"
              subtitle="As per recent scans during the selected period"
              loading={chartsLoading.health}
              isEmpty={healthTotal === 0}
              filterFields={["date", "inspectionType", "site", "obrOrg", "obrBu"]}
              filterValues={healthFilters}
              onFilterChange={setHealthFilters}
              onFilterApply={fetchHealthChart}
              filterOptions={filterOptions}
            >
              <MachineChart machineStatus={siteHealth} isSite={true} compact />
            </SiteChartCard>
          </div>

          <SiteChartCard
            title="Score By Region"
            loading={chartsLoading.region}
            isEmpty={!regionResponseStats?.length}
            filterFields={["date", "inspectionType", "site", "obrOrg", "obrBu"]}
            filterValues={regionFilters}
            onFilterChange={setRegionFilters}
            onFilterApply={fetchRegionChart}
            filterOptions={filterOptions}
            compact
          >
            <BarChart list={regionResponseStats} compact />
          </SiteChartCard>

          <NegativeResponsesV2 questions={questions} loading={loading} isSite={true} />
          <GroupWiseReportV2 list={list} loading={loading} isSite={true} />
        </>
      )}
    </div>
  );
};

export default SiteDashboardV2;
